import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, Search as SearchIcon, Download, Edit, Trash2, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { DELIVERY_OPTIONS, deliveryLabel } from '@/lib/deliveryModes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DispatchReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [editingDispatch, setEditingDispatch] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchCriteria, setSearchCriteria] = useState({
    magazine: '',
    month: '',
    year: '',
    docket_no: '',
    courier_type: '',
    dispatch_date: '',
    dispatched_by: '',
    member_id: '',
    delivery_status: '',
    special_remark: '',
    name: '',
    state: '',
    district: ''
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  // Get current user role and fetch users
  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
    fetchUsers();
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/all-unique-states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchDistricts = async (state) => {
    if (!state) { setDistricts([]); return; }
    try {
      const response = await axios.get(`${API}/all-unique-districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.map(user => user.username));
    } catch (error) {
      console.error('Error fetching users:', error);
      // Fallback to default users if fetch fails
      setUsers(['admin', 'shashi', 'kartik', 'arvind']);
    }
  };

  const fetchDispatches = async (criteria = {}) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/dispatches/search`, criteria);
      setDispatches(response.data);
      setHasSearched(true);
    } catch (error) {
      console.error('Error fetching dispatches:', error);
      toast.error('Failed to fetch dispatch reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchDispatches(searchCriteria);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedDispatches = () => {
    if (!sortColumn) return dispatches;

    return [...dispatches].sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      // Handle date columns
      if (sortColumn === 'dispatch_date') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      // Handle year column
      else if (sortColumn === 'year') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }
      // Handle string columns
      else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleReset = () => {
    setSearchCriteria({
      magazine: '',
      month: '',
      year: '',
      docket_no: '',
      courier_type: '',
      dispatch_date: '',
      dispatched_by: '',
      member_id: '',
      delivery_status: '',
      special_remark: '',
      name: '',
      state: '',
      district: ''
    });
    setDispatches([]);
    setHasSearched(false);
    setSortColumn('');
    setSortDirection('asc');
    setDistricts([]);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEdit = (dispatch) => {
    setEditingDispatch({ ...dispatch });
  };

  const handleCancelEdit = () => {
    setEditingDispatch(null);
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/dispatches/${editingDispatch.id}`, editingDispatch, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Dispatch updated successfully');
      setEditingDispatch(null);
      handleSearch();
    } catch (error) {
      console.error('Error updating dispatch:', error);
      toast.error('Failed to update dispatch');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (dispatchId) => {
    if (!window.confirm('Are you sure you want to delete this dispatch record?')) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dispatches/${dispatchId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      toast.success('Dispatch deleted successfully');
      handleSearch();
    } catch (error) {
      console.error('Error deleting dispatch:', error);
      toast.error('Failed to delete dispatch');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const exportToCSV = () => {
    if (dispatches.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      // Get sorted dispatches to export in the same order as displayed
      const sortedDispatches = getSortedDispatches();
      
      const headers = ['Member ID', 'Name', 'Magazine', 'Month', 'Year', 'Courier Type', 'Docket No.', 'Dispatch Date', 'Status', 'Failure Reason'];
      const csvContent = [
        headers.join(','),
        ...sortedDispatches.map(dispatch => [
          dispatch.member_id || '',
          `"${dispatch.name || ''}"`,
          dispatch.magazine || '',
          dispatch.month || '',
          dispatch.year || '',
          deliveryLabel(dispatch.courier_type) || '',
          dispatch.docket_no || '',
          formatDate(dispatch.dispatch_date),
          dispatch.delivery_status === 'failed' ? 'Failed' : 'Success',
          `"${dispatch.failure_reason || ''}"`
        ].join(','))
      ].join('\\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `Dispatch_Report_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      toast.success('Report downloaded! Check your Downloads folder');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => (new Date().getFullYear() + i - 2).toString());

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 no-print">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: 'var(--primary-blue)' }} data-testid="dispatch-reports-title">
              Dispatch Reports
            </h1>
          </div>
          {dispatches.length > 0 && (
            <div className="flex space-x-2">
              <Button onClick={exportToCSV} className="btn-secondary" data-testid="export-button">
                <Download className="mr-2" size={18} />
                Download Report
              </Button>
              <Button onClick={handlePrint} className="btn-primary" data-testid="print-button">
                <FileText className="mr-2" size={18} />
                Print Report
              </Button>
            </div>
          )}
        </div>

        {/* Search Filters */}
        <div className="form-section mb-8 no-print">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--primary-blue)' }}>
            Search Filters
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <Select
                value={searchCriteria.magazine}
                onValueChange={(value) => setSearchCriteria({ ...searchCriteria, magazine: value })}
              >
                <SelectTrigger data-testid="magazine-filter">
                  <SelectValue placeholder="Select magazine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                  <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Month</Label>
              <Select
                value={searchCriteria.month}
                onValueChange={(value) => setSearchCriteria({ ...searchCriteria, month: value })}
              >
                <SelectTrigger data-testid="month-filter">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Year</Label>
              <Select
                value={searchCriteria.year}
                onValueChange={(value) => setSearchCriteria({ ...searchCriteria, year: value })}
              >
                <SelectTrigger data-testid="year-filter">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Docket Number</Label>
              <Input
                data-testid="docket-filter"
                value={searchCriteria.docket_no}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, docket_no: e.target.value })}
                placeholder="Enter docket number"
              />
            </div>

            <div className="space-y-2">
              <Label>Member ID</Label>
              <Input
                data-testid="member-id-filter"
                value={searchCriteria.member_id}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, member_id: e.target.value })}
                placeholder="Enter member ID"
              />
            </div>

            <div className="space-y-2">
              <Label>Dispatch Date</Label>
              <Input
                type="date"
                data-testid="dispatch-date-filter"
                value={searchCriteria.dispatch_date}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, dispatch_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Dispatched By</Label>
              <Select
                value={searchCriteria.dispatched_by}
                onValueChange={(value) => setSearchCriteria({ ...searchCriteria, dispatched_by: value })}
              >
                <SelectTrigger data-testid="dispatched-by-filter">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user} value={user}>
                      {user}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Courier Type</Label>
              <Select
                value={searchCriteria.courier_type}
                onValueChange={(value) => setSearchCriteria({ ...searchCriteria, courier_type: value })}
              >
                <SelectTrigger data-testid="courier-filter">
                  <SelectValue placeholder="Select courier type" />
                </SelectTrigger>
                <SelectContent>
                  {DELIVERY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Delivery Status</Label>
              <Select
                value={searchCriteria.delivery_status}
                onValueChange={(value) => setSearchCriteria({ ...searchCriteria, delivery_status: value })}
              >
                <SelectTrigger data-testid="delivery-status-filter">
                  <SelectValue placeholder="Select delivery status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="success">✅ Success</SelectItem>
                  <SelectItem value="failed">❌ Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="space-y-2">
              <Label>Special Remark</Label>
              <Input
                data-testid="remark-filter"
                value={searchCriteria.special_remark}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, special_remark: e.target.value })}
                placeholder="Search by remark"
              />
            </div>

            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                data-testid="name-filter"
                value={searchCriteria.name}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, name: e.target.value })}
                placeholder="Search by name"
              />
            </div>

            <div className="space-y-2">
              <Label>State</Label>
              <Select value={searchCriteria.state || 'all_states'} onValueChange={(value) => {
                const newState = value === 'all_states' ? '' : value;
                setSearchCriteria({ ...searchCriteria, state: newState, district: '' });
                setDistricts([]);
                if (newState) fetchDistricts(newState);
              }}>
                <SelectTrigger data-testid="state-filter">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_states">All States</SelectItem>
                  {states.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>District</Label>
              <Select value={searchCriteria.district || 'all_districts'} onValueChange={(value) => setSearchCriteria({ ...searchCriteria, district: value === 'all_districts' ? '' : value })} disabled={!searchCriteria.state}>
                <SelectTrigger data-testid="district-filter">
                  <SelectValue placeholder={searchCriteria.state ? 'All Districts' : 'Select state first'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_districts">All Districts</SelectItem>
                  {districts.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex space-x-4">
            <Button onClick={handleSearch} disabled={loading} className="btn-primary" data-testid="search-button">
              <SearchIcon className="mr-2" size={18} />
              {loading ? 'Searching...' : 'Search'}
            </Button>
            <Button onClick={handleReset} variant="outline" data-testid="reset-button">
              Reset
            </Button>
          </div>
        </div>

        {/* Report Table */}
        {loading ? (
          <div className="loader" />
        ) : (
          <div className="form-section">
            {hasSearched && (
              <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--primary-blue)' }}>
                Total Dispatches: {dispatches.length}
              </h2>
            )}
            {!hasSearched ? (
              <p className="text-gray-600 text-center">Use search filters above to find dispatch records.</p>
            ) : dispatches.length === 0 ? (
              <p className="text-gray-600 text-center">No dispatch records found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="dispatch-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('member_id')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Member ID {sortColumn === 'member_id' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Name {sortColumn === 'name' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('magazine')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Magazine {sortColumn === 'magazine' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('month')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Month {sortColumn === 'month' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('year')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Year {sortColumn === 'year' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('courier_type')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Courier Type {sortColumn === 'courier_type' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('docket_no')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Docket No. {sortColumn === 'docket_no' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('dispatch_date')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Dispatch Date {sortColumn === 'dispatch_date' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('delivery_status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Status {sortColumn === 'delivery_status' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th onClick={() => handleSort('special_remark')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                        Special Remark {sortColumn === 'special_remark' && <span style={{ marginLeft: '4px' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </th>
                      <th className="no-print">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getSortedDispatches().map((dispatch) => (
                      <tr key={dispatch.id} data-testid={`dispatch-row-${dispatch.member_id}`}>
                        <td>{dispatch.member_id}</td>
                        <td>{dispatch.name}</td>
                        <td>{dispatch.magazine}</td>
                        <td>{dispatch.month}</td>
                        <td>{dispatch.year ? String(dispatch.year).replace('.0', '') : ''}</td>
                        <td>{deliveryLabel(dispatch.courier_type)}</td>
                        <td>{dispatch.docket_no}</td>
                        <td>
                          <div>
                            {formatDate(dispatch.dispatch_date)}
                            {dispatch.created_by && (
                              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                                by {dispatch.created_by}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <span 
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              dispatch.delivery_status === 'failed' 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {dispatch.delivery_status === 'failed' ? '❌ Failed' : '✅ Success'}
                          </span>
                          {dispatch.delivery_status === 'failed' && dispatch.failure_reason && (
                            <div style={{ fontSize: '10px', color: '#dc2626', marginTop: '4px' }}>
                              {dispatch.failure_reason}
                            </div>
                          )}
                        </td>
                        <td style={{ maxWidth: '150px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                          {dispatch.special_remark || '-'}
                        </td>
                        <td className="no-print">
                          <div className="flex space-x-2">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(dispatch)}>
                              <Edit size={16} className="mr-1" />
                              Edit
                            </Button>
                            {userRole === 'admin' && (
                              <Button size="sm" variant="outline" onClick={() => handleDelete(dispatch.id)} className="text-red-600 hover:text-red-700">
                                <Trash2 size={16} className="mr-1" />
                                Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingDispatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ color: '#7c3aed' }}>Edit Dispatch</h2>
              <Button variant="outline" onClick={handleCancelEdit} size="sm">
                <X size={18} />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Member ID</Label>
                <Input value={editingDispatch.member_id} disabled className="bg-gray-100" />
              </div>

              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={editingDispatch.name} onChange={(e) => setEditingDispatch({ ...editingDispatch, name: e.target.value })} />
              </div>

              <div className="space-y-2">
                <Label>Magazine</Label>
                <Select value={editingDispatch.magazine} onValueChange={(value) => setEditingDispatch({ ...editingDispatch, magazine: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Aabkari Times">Aabkari Times</SelectItem>
                  <SelectItem value="Aabkari Times ENG">Aabkari Times ENG</SelectItem>
                    <SelectItem value="Sugar Times">Sugar Times</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Month</Label>
                <Select value={editingDispatch.month} onValueChange={(value) => setEditingDispatch({ ...editingDispatch, month: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="January">January</SelectItem>
                    <SelectItem value="February">February</SelectItem>
                    <SelectItem value="March">March</SelectItem>
                    <SelectItem value="April">April</SelectItem>
                    <SelectItem value="May">May</SelectItem>
                    <SelectItem value="June">June</SelectItem>
                    <SelectItem value="July">July</SelectItem>
                    <SelectItem value="August">August</SelectItem>
                    <SelectItem value="September">September</SelectItem>
                    <SelectItem value="October">October</SelectItem>
                    <SelectItem value="November">November</SelectItem>
                    <SelectItem value="December">December</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Year</Label>
                <Input type="number" value={editingDispatch.year} onChange={(e) => setEditingDispatch({ ...editingDispatch, year: parseInt(e.target.value) })} />
              </div>

              <div className="space-y-2">
                <Label>Courier Type</Label>
                <Select value={editingDispatch.courier_type || ''} onValueChange={(value) => setEditingDispatch({ ...editingDispatch, courier_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select courier type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DELIVERY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Docket No</Label>
                <Input value={editingDispatch.docket_no} onChange={(e) => setEditingDispatch({ ...editingDispatch, docket_no: e.target.value })} />
              </div>

              <div className="space-y-2">
                <Label>Delivery Status</Label>
                <Select value={editingDispatch.delivery_status || 'success'} onValueChange={(value) => setEditingDispatch({ ...editingDispatch, delivery_status: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="success">✅ Success</SelectItem>
                    <SelectItem value="failed">❌ Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {editingDispatch.delivery_status === 'failed' && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Failure Reason (Optional)</Label>
                  <Input 
                    value={editingDispatch.failure_reason || ''} 
                    onChange={(e) => setEditingDispatch({ ...editingDispatch, failure_reason: e.target.value })} 
                    placeholder="Enter reason for failure"
                  />
                </div>
              )}

              <div className="space-y-2 md:col-span-2">
                <Label>Special Remark</Label>
                <Input 
                  value={editingDispatch.special_remark || ''} 
                  onChange={(e) => setEditingDispatch({ ...editingDispatch, special_remark: e.target.value })} 
                  placeholder="e.g., Bill created, Complimentary copy, etc."
                />
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-4">
              <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={loading} style={{ backgroundColor: '#7c3aed', color: 'white' }}>
                <Save className="mr-2" size={18} />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm;
          }
          
          .no-print {
            display: none !important;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          table {
            font-size: 9px !important;
            width: 100% !important;
          }
          
          th, td {
            padding: 3px !important;
            font-size: 9px !important;
          }
          
          .form-section {
            page-break-inside: avoid;
          }
          
          * {
            color: black !important;
          }
          
          h1, h2 {
            color: black !important;
          }
          
          th {
            background-color: white !important;
            color: black !important;
            border: 1px solid black !important;
          }
          
          td {
            border: 1px solid #ccc !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DispatchReports;